import { WorldMaker } from ".";
import { CharacterBehavior } from "./CharacterBehavior";
import { RapierSystemBehavior } from "./RapierSystemBehavior";
import { TransformBehavior } from "./TransformBehavior";
import { AttachmentRule, Behavior } from "./types";
import * as RAPIER from "@dimforge/rapier3d-compat";

  export class PlayerControlsBehavior extends Behavior {
    private characterBehavior: CharacterBehavior | null = null;
    private transformBehavior: TransformBehavior | null = null;
    private movementSpeed: number = 5; // units per second
    private jumpForce: number = 8; // jump impulse strength
    private forceMultiplier: number = 0.1; // Force multiplier for direct physics control
    private controlsMovementHandler: (event: Event) => void;
    private currentMovementVector: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };
    private isGrounded: boolean = false;
    private groundCheckDistance: number = 0.1; // How far to check for ground
    private lastJumpPressed: boolean = false;

    static getAttachmentRules(): AttachmentRule[] {
      return [{ type: "class", selector: "player-controls" }];
    }

    constructor(element: Element, world: WorldMaker) {
      super(element, world);
      this.controlsMovementHandler = this.handleControlsMovement.bind(this);
    }

    onAttach(): void {
      console.log("PlayerControlsBehavior attached to:", this.element);

      // Get reference to CharacterBehavior on the same element (preferred method)
      this.characterBehavior = this.element.getBehavior(CharacterBehavior);

      // Get reference to TransformBehavior (fallback method)
      this.transformBehavior = this.element.getBehavior(TransformBehavior);

      if (!this.characterBehavior && !this.transformBehavior) {
        console.error(
          "PlayerControlsBehavior: Neither CharacterBehavior nor TransformBehavior found on element. Player controls require at least one of these behaviors to be attached.",
        );
        return;
      }

      if (this.characterBehavior) {
        console.log("PlayerControlsBehavior: Using CharacterBehavior for movement");
      } else if (this.transformBehavior) {
        console.log("PlayerControlsBehavior: Using TransformBehavior for movement (fallback mode)");
        // Ensure the element has a rigidbody for physics-based movement
        if (!this.transformBehavior.hasOwnRigidBody()) {
          console.warn(
            "PlayerControlsBehavior: TransformBehavior does not have a rigidbody. Physics movement may not work correctly.",
          );
        }
      }

      // Initialize movement speed from element attribute if present
      const speedAttr = this.element.getAttribute("movement-speed");
      if (speedAttr) {
        const speed = parseFloat(speedAttr);
        if (!isNaN(speed) && speed > 0) {
          this.movementSpeed = speed;
        }
      }

      // Initialize jump force from element attribute if present
      const jumpAttr = this.element.getAttribute("jump-force");
      if (jumpAttr) {
        const force = parseFloat(jumpAttr);
        if (!isNaN(force) && force > 0) {
          this.jumpForce = force;
        }
      }

      // Initialize force multiplier from element attribute if present
      const forceMultiplierAttr = this.element.getAttribute("force-multiplier");
      if (forceMultiplierAttr) {
        const multiplier = parseFloat(forceMultiplierAttr);
        if (!isNaN(multiplier) && multiplier > 0) {
          this.forceMultiplier = multiplier;
        }
      }

      // Listen for control probe movement events
      this.element.addEventListener("controlsmovement", this.controlsMovementHandler);

      // Also listen on the control probe child if it exists
      const controlProbe = this.element.querySelector("m-control-probe");
      if (controlProbe) {
        controlProbe.addEventListener("controlsmovement", this.controlsMovementHandler);
      }

      // Make sure the character uses the jump force from player controls
      this.element.setAttribute("jump-force", this.jumpForce.toString());
    }

    onDetach(): void {
      console.log("PlayerControlsBehavior detached from:", this.element);

      // Remove event listeners
      this.element.removeEventListener("controlsmovement", this.controlsMovementHandler);

      const controlProbe = this.element.querySelector("m-control-probe");
      if (controlProbe) {
        controlProbe.removeEventListener("controlsmovement", this.controlsMovementHandler);
      }

      // Stop movement
      this.currentMovementVector = { x: 0, y: 0, z: 0 };
    }

    private getTransformBehavior(): TransformBehavior | null {
      if (!this.transformBehavior) {
        this.transformBehavior = this.element.getBehavior(TransformBehavior);
      }
      return this.transformBehavior;
    }

    private checkGrounded(): boolean {
      const rigidBody = TransformBehavior.getRigidBodyForElement(this.element);
      if (!rigidBody) return false;

      const physicsWorld = RapierSystemBehavior.getPhysicsWorld();
      if (!physicsWorld) return false;

      const currentPos = rigidBody.translation();
      const characterCollider = TransformBehavior.getColliderForElement(this.element);

      // Cast a ray downward to check for ground
      const rayOrigin = currentPos;
      const rayDirection = { x: 0, y: -1, z: 0 };
      const ray = new RAPIER.Ray(rayOrigin, rayDirection);

      // Check slightly below the character's bottom
      const hit = physicsWorld.castRay(
        ray,
        this.groundCheckDistance + 0.5, // Check a bit further down
        true,
        undefined,
        undefined,
        characterCollider || undefined,
      );

      return hit !== null && hit.timeOfImpact <= this.groundCheckDistance + 0.5;
    }

    private handleControlsMovement(event: Event): void {
      const customEvent = event as CustomEvent;
      const { movement, keysPressed } = customEvent.detail;

      // Update current movement vector
      if (movement && typeof movement === "object") {
        this.currentMovementVector = {
          x: movement.x || 0,
          y: movement.y || 0,
          z: movement.z || 0,
        };
      } else {
        this.currentMovementVector = { x: 0, y: 0, z: 0 };
      }
    }

    private applyMovementWithTransformBehavior(
      movementVector: { x: number; y: number; z: number },
      deltaTime: number,
      speed: number,
    ): void {
      const transformBehavior = this.getTransformBehavior();
      if (!transformBehavior) return;

      const rigidBody = transformBehavior.getRigidBody();
      if (!rigidBody) return;

      // Check if we're grounded for jump logic
      this.isGrounded = this.checkGrounded();

      // Calculate movement force based on input and speed
      const movementForce = {
        x: movementVector.x * speed * this.forceMultiplier,
        y: 0, // Don't apply Y force for regular movement
        z: movementVector.z * speed * this.forceMultiplier,
      };

      // Apply horizontal movement force
      if (Math.abs(movementForce.x) > 0.1 || Math.abs(movementForce.z) > 0.1) {
        transformBehavior.applyForce(movementForce);
      }

      // Handle jumping
      const shouldJump = movementVector.y > 0.5;
      if (shouldJump && !this.lastJumpPressed && this.isGrounded) {
        const jumpImpulse = {
          x: 0,
          y: this.jumpForce,
          z: 0,
        };
        transformBehavior.applyImpulse(jumpImpulse);
        console.log("PlayerControlsBehavior: Applied jump impulse via TransformBehavior");
      }
      this.lastJumpPressed = shouldJump;

      // Apply some damping to prevent infinite acceleration
      const currentVelocity = rigidBody.linvel();
      const dampingFactor = 0.9; // Reduce velocity by 10% each frame for horizontal movement

      // Only apply damping to horizontal movement, preserve vertical velocity for gravity/jumping
      const dampedVelocity = {
        x: currentVelocity.x * dampingFactor,
        y: currentVelocity.y, // Don't damp vertical velocity
        z: currentVelocity.z * dampingFactor,
      };

      // Only apply damping if we're not actively moving
      if (Math.abs(movementVector.x) < 0.1 && Math.abs(movementVector.z) < 0.1) {
        transformBehavior.setVelocity(dampedVelocity);
      }
    }

    tick(deltaTime: number): void {
      // Check if movement speed has changed
      const speedAttr = this.element.getAttribute("movement-speed");
      if (speedAttr) {
        const speed = parseFloat(speedAttr);
        if (!isNaN(speed) && speed > 0 && speed !== this.movementSpeed) {
          this.movementSpeed = speed;
        }
      }

      // Check if jump force has changed
      const jumpAttr = this.element.getAttribute("jump-force");
      if (jumpAttr) {
        const force = parseFloat(jumpAttr);
        if (!isNaN(force) && force > 0 && force !== this.jumpForce) {
          this.jumpForce = force;
          // Update the character behavior's jump force
          this.element.setAttribute("jump-force", this.jumpForce.toString());
        }
      }

      // Check if force multiplier has changed (for TransformBehavior mode)
      const forceMultiplierAttr = this.element.getAttribute("force-multiplier");
      if (forceMultiplierAttr) {
        const multiplier = parseFloat(forceMultiplierAttr);
        if (!isNaN(multiplier) && multiplier > 0 && multiplier !== this.forceMultiplier) {
          this.forceMultiplier = multiplier;
        }
      }

      // Apply movement - prefer CharacterBehavior, fallback to TransformBehavior
      if (this.characterBehavior) {
        // Use CharacterBehavior for movement (preferred method)
        this.characterBehavior.applyMovement(
          this.currentMovementVector,
          deltaTime,
          this.movementSpeed,
        );
      } else if (this.transformBehavior) {
        // Use TransformBehavior for movement (fallback method)
        this.applyMovementWithTransformBehavior(
          this.currentMovementVector,
          deltaTime,
          this.movementSpeed,
        );
      }
    }
  }
